import { useAppContext, useAssessmentContext } from '../context';
import IAnswer from '../models/IAnswer';
import IAssessment from '../models/IAssessment';
import IAssessmentElement, { AssessmentElementType } from '../models/IAssessmentElement';
import IAssessmentPage from '../models/IAssessmentPage';
import ICompletedAssessment from '../models/ICompletedAssessment';
import AssessmentService from '../services/AssessmentService';

import AssessmentHelpers from './AssessmentHelpers';
import AssessmentLocalStorageManager from './AssessmentLocalStorageManager';
import Common from './Common';
import useQuestionManager from './useQuestionManager';

export default function useAssessmentManager() 
{
	const appContext = useAppContext();
	const assessmentContext = useAssessmentContext();
	const questionManager = useQuestionManager();

	const loadAssessment = async (assessmentId: string): Promise<IAssessment | null> => 
	{
		if (!assessmentId)
			return null;

		const existingAssessment = assessmentContext.assessment;
		if (existingAssessment?.assessmentId === assessmentId)
			return existingAssessment; // already loaded

		const assessmentService = new AssessmentService();

		let assessment = AssessmentLocalStorageManager.getAssessment(assessmentId);
		if (!assessment) 
		{
			//no stored assessment - get latest version instead
			assessment = await assessmentService.getAssessment(assessmentId, null)
				.catch(e => 
				{
					console.error(e);
					return null;
				});

			//store assessment for future use
			if (assessment)
				AssessmentLocalStorageManager.setAssessment(assessment);
		}

		if (!assessment)
			return null;

		//Order assessment on Indexes
		assessment.pages = assessment.pages.sort((a, b) => a.index - b.index)
		assessment.pages.forEach(page => 
		{
			page.headerSection = page.headerSection.sort((a, b) => a.index - b.index);
			page.headerSection.forEach(element => 
			{
				element.options = element.options?.sort((a, b) => a.index - b.index) ?? null;
			})
			page.bodySection = page.bodySection.sort((a, b) => a.index - b.index);
			page.bodySection.forEach(element => 
			{
				element.options = element.options?.sort((a, b) => a.index - b.index) ?? null;
			})
			page.footerSection = page.footerSection.sort((a, b) => a.index - b.index);
			page.footerSection.forEach(element => 
			{
				element.options = element.options?.sort((a, b) => a.index - b.index) ?? null;
			})
		})

		assessmentContext.setAssessment(assessment);

		return assessment;
	}

	const handleMovePage = async (pageDelta: number): Promise<boolean> => 
	{
		if (!assessmentContext.assessment)
			return false;

		const nextIndex = assessmentContext.pageIndex + pageDelta;
		const nextPage = assessmentContext.assessment.pages[nextIndex];

		if (nextPage) 
		{
			AssessmentLocalStorageManager.setPageIndex(assessmentContext.assessment.assessmentId, nextIndex);
			appContext.navigateToAssessmentPage(assessmentContext.assessment.assessmentId, nextIndex);
		}
		else 
		{
			if (assessmentContext.isLastPage) 
			{
				// submit the assessment
				appContext.setIsLoading(true);

				const submitResult = await submitAssessment()
					.then(result => 
					{
						appContext.setIsLoading(false);

						if (!result)
							return false;

						return true;
					});

				return submitResult;
			}
		}

		return true;
	}

	const submitAssessment = async (): Promise<boolean> => 
	{
		if (!assessmentContext.assessment)
			return false;

		//Only process questions that were shown
		const allAssessmentQuestions = getAllAssessmentQuestions(assessmentContext.assessment)
			.filter(q => q.isAlwaysShown || questionManager.showElement(q.showConditionSource ?? '', q.showConditionValue ?? ''))

		const assessmentId = assessmentContext.assessment?.assessmentId ?? '';
		const answers = allAssessmentQuestions.map(q => 
		{
			const answer: IAnswer =
            {
            	id: q.id,
            	value: AssessmentLocalStorageManager.getAnswerValues(assessmentId, q.id)
            };

			if (q.type === 'NUMERIC')
				answer.value = answer.value.map((i: any) => Number(i as string)); // number validation need the values as numbers, not strings

			//Policy Questions require additional fields to be sent over in order to do the secondary lookup if multiple policies are returned
			if (q.type === 'POLICY_POSTCODE' || q.type === 'POLICY_DOB' || q.type === 'POLICY_EMAIL') 
			{
				const policyNumber = AssessmentLocalStorageManager.getAnswerValues(assessmentId, q.id, 'PolicyNumber')[0];
				const postcode = AssessmentLocalStorageManager.getAnswerValues(assessmentId, q.id, 'Postcode')[0];
				const dateOfBirth = AssessmentLocalStorageManager.getAnswerValues(assessmentId, q.id, 'DateOfBirth')[0];
				const emailAddress = AssessmentLocalStorageManager.getAnswerValues(assessmentId, q.id, 'EmailAddress')[0];

				answer.value = [
					'POLICY_ID:' + (answer.value[0]?.toString() ?? ''),
					'POLICY_NUMBER:' + (policyNumber ?? ''),
					'POLICY_POSTCODE:' + (postcode ?? ''),
					'POLICY_DOB:' + (dateOfBirth ?? ''),
					'POLICY_EMAIL:' + (emailAddress ?? '')
				]
			}

			return answer;
		});

		const completedAssessment: ICompletedAssessment =
        {
        	id: assessmentContext.assessment.assessmentId,
        	assessmentVersion: assessmentContext.assessment.version,
        	sessionId: Common.newGuid(),
        	answers: answers
        };

		const assessmentService = new AssessmentService();
		const result = await assessmentService.submitAssessment(completedAssessment)
			.catch(e => 
			{
				console.error(e);
				return false;
			});

		if (!result)
			return false;

		handleSuccessfulSubmit();

		return true;
	}

	const getAllAssessmentQuestions = (assessment: IAssessment): IAssessmentElement[] =>
		AssessmentHelpers.getAllAssessmentQuestions(assessment);

	const getAllPageQuestions = (page: IAssessmentPage | null): IAssessmentElement[] =>
		AssessmentHelpers.getAllPageQuestions(page);

	const getPolicyId = (): string => 
	{
		if (!assessmentContext.assessment)
			return '0';

		const assessmentId = assessmentContext.assessment.assessmentId

		const policyIdArray: string[] = []

		getAllAssessmentQuestions(assessmentContext.assessment).forEach(question => 
		{
			if (isPolicyQuestion(question.type))
				policyIdArray.push(AssessmentLocalStorageManager.getAnswerValues(assessmentId, question.id)[0] ?? '0')
		})

		return policyIdArray[0] ?? '0'
	}

	const isPolicyQuestion = (questionType: AssessmentElementType | undefined) => 
		(questionType === 'POLICY_POSTCODE' || questionType === 'POLICY_DOB' || questionType === 'POLICY_EMAIL')

	const handleSuccessfulSubmit = () => 
	{
		AssessmentLocalStorageManager.clear(assessmentContext.assessment?.assessmentId ?? '');
		appContext.navigateToAssessmentCompletePage();
	}

	return {
		loadAssessment, submitAssessment, handleMovePage,
		getAllAssessmentQuestions,
		getAllPageQuestions,
		getPolicyId
	} as const;
}
